import CryptoJS from 'crypto-js';

import {
  REDUX_CLIENTCODE,
  REDUX_JWT,
  REDUX_PIN,
  REDUX_SESSIONKEY,
  REDUX_USER,
  REDUX_USERNAME,
} from 'constants/persistence';

/**
 * Used to make it not obvious that we store user credentials in local storage
 * and to make obtaining the data slightly harder.
 * Only implemented as a means to pass SOC certification.
 * Is meant to be used to store data that can be used to authenticate: username, pin, session key, jwt.
 * Should be replaced with an actually secure solution.
 */
export class ObfuscatedLocalStorage {
  private static encryptionKey = 'wN!0T!Tga/0P3c/KK@Ezw.=uBWE*qAkd';

  private static stateKey = 'REDUX-userData';

  private static encrypt(decryptedText: string): string {
    return CryptoJS.AES.encrypt(decryptedText, this.encryptionKey).toString();
  }

  private static decrypt(encryptedText: string): string {
    const bytes = CryptoJS.AES.decrypt(encryptedText, this.encryptionKey);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  private static getState() {
    const data = localStorage.getItem(this.stateKey);
    if (data === null) {
      return null;
    }
    try {
      const decryptedData = this.decrypt(data);
      return JSON.parse(decryptedData);
    } catch (error) {
      return null;
    }
  }

  private static setState(data: Record<string, any>) {
    const encryptedData = this.encrypt(JSON.stringify(data));
    localStorage.setItem(this.stateKey, encryptedData);
  }

  static getItem(key: string) {
    const state = this.getState();
    return state?.[key] ?? null;
  }

  static setItem(key: string, data: string) {
    const state = this.getState();
    if (state) {
      this.setState({ ...state, [key]: data });
    } else {
      this.setState({ [key]: data });
    }
  }

  static removeItem(key: string) {
    const state = this.getState();
    if (state) {
      delete state[key];
      this.setState(state);
    }
  }
}

// Because older versions of the POS will still be using unobfuscated local storage
// and version control means some users may be logging in on an old version only
// to be redirected, check for existing data from older versions and apply/remove it

// This will match the user expectation of "the most recent login is the one that counts"
// Moving from old version to new will take on the login from the old
// Moving from new to old will require the user to log in again

const LEGACY_USER_DATA_KEYS = [
  REDUX_CLIENTCODE,
  REDUX_SESSIONKEY,
  REDUX_JWT,
  REDUX_USERNAME,
  REDUX_PIN,
  REDUX_USER,
];
if (LEGACY_USER_DATA_KEYS.some(k => localStorage.getItem(k) !== null)) {
  // We have legacy data, which means that the last time the POS was used, it was in a version with the old systen
  // Therefore drop any cached data in the new system and copy the more recent data from the old one
  // 1: Copy over the relevant data
  LEGACY_USER_DATA_KEYS.forEach(k => {
    const value = localStorage.getItem(k);
    if (value !== null) {
      ObfuscatedLocalStorage.setItem(k, value);
    }
  });
  // 2: Delete it
  LEGACY_USER_DATA_KEYS.forEach(k => {
    localStorage.removeItem(k);
  });
}
